import {
  FilterParams,
  GenericObject,
  PaginatedParams
} from "src/library/utils";
import { IAddress } from "src/library/components/Address";
import useAxios from "src/core/shared/hooks/useAxios";
import {
  CustomerDropStatuses,
  ICustomer
} from "src/core/customers/api/customers.api";
import { PaginatedData } from "src/core/shared/api";
import { IPriceCode } from "src/core/shared/api/price-change.api";
import {
  EndpointConfig,
  RequestConfig,
  UseApiRequest
} from "src/core/shared/api/config.api";
import { IUser } from "src/core/users/api/users.api";
import { IShutdown } from "src/modules/shutdowns/api/shutdown.api";
import { IOrderVariant } from "src/core/orders/api/orders.api";
import cacheMinutesToMilliseconds from "src/library/utils/time-helpers/cache-minutes-to-milliseconds";

export type CreateDropParams = Pick<
  IDrop,
  "customerId" | "branchNumber" | "name" | "address" | "contactName" | "contactNumber" | "contactEmail" | "priceCodeId" | "customData"
>;
export type UpdateDropParams = Partial<IDrop> & { id: number };
export type BulkUpdateDropParams = {update: Partial<IDrop>; branchIds: number[]};
export type ListDropsParams = { pagination: PaginatedParams<IDrop> | GenericObject; filters: FilterParams<IDrop> };
export type ListDropsByCustomerParams = { customerId: number;} & Partial<ListDropsParams>;
export type ListUserAssignableDrops = { userId: number;} & Partial<ListDropsParams>;

export type ListUserDropsParams = { userId: number; filters: FilterParams<IDrop>; pagination: PaginatedParams<IDrop> };
export type ListUserDropsResponse = PaginatedData<IDrop> & { total: number };
export type ListUserOrderableDrops = {orderVariantId: number; filters: FilterParams<IDrop>; pagination: PaginatedParams<IDrop>};

export type UpdatedDropStatusParams = {
  branchId: number;
  status: IDrop["status"];
  statusReason?: IDrop["statusReason"];
  cancelOrders: boolean;
};
type DropEndpointConfig = {
  LIST_DROPS: EndpointConfig<
    ListDropsParams,
    PaginatedData<IDrop>
  >;
  LIST_DROPS_BY_CUSTOMER: EndpointConfig<
    ListDropsByCustomerParams,
    PaginatedData<IDrop>
  >;
  LIST_USER_ASSIGNABLE_DROPS: EndpointConfig<
    ListUserAssignableDrops,
    PaginatedData<IDrop>
  >;
  GET_USER_DROPS: EndpointConfig<
    ListUserDropsParams,
    ListUserDropsResponse
  >;
  GET_DROP: EndpointConfig<
    number,
    IDrop
  >;

  CREATE_DROP: EndpointConfig<
    CreateDropParams,
    IDrop
  >;

  UPDATE_DROP: EndpointConfig<
    UpdateDropParams,
    IDrop
  >;
  BULK_UPDATE_DROP: EndpointConfig<
    BulkUpdateDropParams,
    number
  >;
  UPDATE_DROP_STATUS: EndpointConfig<
    UpdatedDropStatusParams,
    {message: string}
  >;
  LIST_USER_ORDERABLE_DROPS: EndpointConfig<ListUserOrderableDrops, PaginatedData<IDrop>>
};

const requests: RequestConfig<DropEndpointConfig> = {
  LIST_DROPS: params => ({
    url: `${process.env.REACT_APP_API_URL}/core/branches`,
    method: "GET",
    params
  }),
  LIST_DROPS_BY_CUSTOMER: params => ({
    url: `${process.env.REACT_APP_API_URL}/core/branches/customer/${params.customerId}`,
    method: "GET",
    params
  }),
  LIST_USER_ASSIGNABLE_DROPS: params => ({
    url: `${process.env.REACT_APP_API_URL}/core/branches/assignable-user/${params.userId}`,
    method: "GET",
    params
  }),
  GET_USER_DROPS: params => ({
    url: `${process.env.REACT_APP_API_URL}/core/branches/user/${params.userId}`,
    method: "GET",
    params
  }),
  GET_DROP: id => ({
    url: `${process.env.REACT_APP_API_URL}/core/branches/${id}`,
    method: "GET",
    cache: {
      key: req => req.url + id.toString(),
      maxAge: cacheMinutesToMilliseconds(2)
    }
  }),
  CREATE_DROP: data => ({
    url: `${process.env.REACT_APP_API_URL}/seedless/branches`,
    method: "POST",
    data
  }),
  UPDATE_DROP: data => ({
    url: `${process.env.REACT_APP_API_URL}/seedless/branches/${data.id}`,
    method: "PATCH",
    data
  }),
  BULK_UPDATE_DROP: data => ({
    url: `${process.env.REACT_APP_API_URL}/core/branches/bulk-update`,
    method: "PATCH",
    data
  }),
  UPDATE_DROP_STATUS: ({ branchId, ...data }) => ({
    url: `${process.env.REACT_APP_API_URL}/seedless/branches/${branchId}/status`,
    method: "POST",
    data
  }),
  LIST_USER_ORDERABLE_DROPS: params => ({
    url: `${process.env.REACT_APP_API_URL}/core/branches/user-orderable/all`,
    method: "GET",
    params
  })
};

export const useDropsApi: UseApiRequest<DropEndpointConfig> = (name, config) => useAxios(requests[ name ], name, config);

export type CustomData = Array<{ name: string; description: string; value: string }>;

export type BranchType = "standard" | "bulk";
export interface IDrop {
  id: number;
  branchNumber: string;
  name: string;
  address: IAddress;
  priceCodeId: number;
  priceCode?: IPriceCode;
  /** RJL special price code */
  specialPriceCodeId?: number | null;
  customerId: number;
  status: CustomerDropStatuses;
  statusReason: string;
  customer?: ICustomer;
  contactName: string;
  contactNumber: string;
  contactEmail: string;
  deliveryCode?: string;
  deliveryDescription?: string;
  deliveryInstructions?: string;
  createdAt: Date;
  updatedAt: Date;
  settings?: IInheritedSetting[];
  groupedSettings: GroupedSettings;
  customData?: CustomData;
  defaultPaymentCardId?: number;
  productApprovalListId?: number | null;
  vanSalesOrdering?: boolean;
  accountManagerId?: number | null;
  accountManager?: IUser;
  discountTypeId?: number | null;
  discountType?: IDiscountType;
  discount: number;
  shutdowns?: IShutdown[];
  orderLastUpdatedAt?: Date;
  type: BranchType;
  fulfilment: boolean;
  supplierBranchNumber?: string;
  /** Not a persisted value, this is worked out on the API based on products available to the drop */
  variantsAvailable?: IOrderVariant[];
}

export type GroupedSettings = {
  delivery_cutoff?: IInheritedSetting<"delivery_cutoff">;
  minimum_order_quantity?: IInheritedSetting<"minimum_order_quantity">;
  minimum_order_value?: IInheritedSetting<"minimum_order_value">;
  delivery_route?: IInheritedSetting<"delivery_route">;
  delivery_week_leadtimes?: IInheritedSetting<"delivery_week_leadtimes">;
  one_daily_delivery?: IInheritedSetting<"one_daily_delivery">;
  price_visible?: IInheritedSetting<"price_visible">;
  order_mode?: IInheritedSetting<"order_mode">;
  order_data_requirements?: IInheritedSetting<"order_data_requirements">;
  point_of_contact?: IInheritedSetting<"point_of_contact">;
  require_payment_method?: IInheritedSetting<"require_payment_method">;
  allow_substitutions?: IInheritedSetting<"allow_substitutions">;
  allow_waste_recording?: IInheritedSetting<"allow_waste_recording">;
  allow_holidays?: IInheritedSetting<"allow_holidays">;
  allow_order_customer_comments?: IInheritedSetting<"allow_order_customer_comments">;

};

export interface IDeliveryCutoffSetting {
  time: string;
}

export interface IOrderDataRequired {
  orderPONumber: boolean | DataRequirement;
}

export type DataRequirement = {
  required: boolean;
  regex?: string;
  message: string;
  maxLength?: number;
  unique?: boolean;
};
export interface IMinimumOrderQuantitySetting {
  quantity: number;
  /** typed as such because key would reference a packsize code */
  [key: string]: number;
}

export interface IMinimumOrderValueSetting {
  value: number;
}

export interface IOrderModeSetting {
  allow: "DL" | "MTO" | "ANY" ;
}
export interface IDeliveryWeekLeadTimesSetting {
  deliveryWeekLeadTimes: { [weekDay: string]: ILeadTimeDeliveryWeek };
}

export type ILeadTimeDeliveryWeek = {
  [weekDay: string]: DeliveryWeekLeadTime;
};

export type WeekDay = 1 | 2 | 3 | 4 | 5 | 6 | 7;
export type WeekDayString = "1" | "2" | "3" | "4" | "5" | "6" | "7";

export type DeliveryWeekLeadTime = {
  /** Delivery day to cutoff at */
  day: WeekDay;
  /** Delivery week to cutoff at @default 0 - ie same week */
  week?: number;
  /** The time to cutoff at */
  cutoffTime?: string;
};

export type OrderedInheritedSettings<T> = { [P in InheritedSettingNameType]?: T };

export interface IInheritedSetting<S extends InheritedSettingNameType = InheritedSettingNameType> {
  entityId?: number;
  id: number;
  name: S;
  scope: InheritedSettingScopeType;
  value: InheritedSettingValue[S];
  secondaryEntityId?: number;
  secondaryScope?: "delivery_route";
  deliveryRoute?: IDeliveryRoute;
  customer?: ICustomer;
  branch?: IDrop;
}

export interface InheritedSettingValue {
  delivery_cutoff: IDeliveryCutoffSetting;
  minimum_order_quantity: IMinimumOrderQuantitySetting;
  minimum_order_value: IMinimumOrderValueSetting;
  delivery_week_leadtimes: IDeliveryWeekLeadTimesSetting;
  order_mode: IOrderModeSetting;
  delivery_route: null;
  one_daily_delivery: { value: boolean };
  price_visible: { visible: boolean };
  require_payment_method: { required: boolean; payBy: PaymentSettingPayBy; };
  order_data_requirements: IOrderDataRequired;
  point_of_contact: PointOfContact;
  allow_substitutions: { allowed: boolean };
  allow_waste_recording: { allowed: boolean };
  allow_holidays: { allowed: boolean };
  allow_order_customer_comments: { allowed: boolean };
  credit_request_auto_approval: CreditRequestAutoApprovalSetting;
  billing_type: BillingTypeSetting;
}

export type BillingType = "proforma" | "invoice";
export interface BillingTypeSetting {
  type: BillingType;
}

export enum PaymentSettingPayBy {
  Cutoff = "cut_off",
  Invoice = "invoice",
  Any = "any"
}

export interface PointOfContact {
  contactName?: string;
  address?: Address;
  contactEmail?: string;
  contactNumber?: string;
  website?: string;
}

export interface CreditRequestAutoApprovalSetting {
  enabled: boolean;
  threshold?: number;
  thresholds?: CreditRequestApprovalThreshold[];
}

export interface CreditRequestApprovalThreshold {
  /** Code should match code CreditRequestReasonModel */
  reasonCode: string;
  /** percentage */
  autoApprovalThreshold: number;
}

export interface Address {
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  city?: string;
  postcode?: string;
}

export type InheritedSettingNameType = keyof InheritedSettingValue;
export type InheritedSettingScopeType = "global" | "customer" | "branch";

export type DeliveryRouteType = "global" | "branch" | "customer" | "product";
export type WeekDayName = keyof IDeliveryDays;
export interface IDeliveryRoute {
  id: number;
  type: DeliveryRouteType;
  name: string | null;
  deliveryDays: IDeliveryDays;
  entityId: number | null;
}

export interface IDeliveryDays {
  Monday?: 1;
  Tuesday?: 2;
  Wednesday?: 3;
  Thursday?: 4;
  Friday?: 5;
  Saturday?: 6;
  Sunday?: 7;
}

export interface IDiscountType {
  id: number;
  action: DiscountTypeAction;
  description: string;
}

export enum DiscountTypeAction {
  PercentageByLine = "percentage_by_line",
  // PercentageByInvoiceValue = "percentage_by_invoice_value"
}
